import { useState, useMemo, useEffect } from 'react'
import { MapContainer, TileLayer, Marker, Popup, useMapEvents, useMap } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import {
  Box,
  Button,
  Typography,
  Alert,
  Snackbar,
  Paper,
  Stack,
  Autocomplete,
  TextField,
  CircularProgress,
} from '@mui/material'
import { useInvestmentContext } from '../contexts/InvestmentContext'
import { supabase } from '../supabaseClient'
import { searchGeocode, type GeocodeResult, type GeocodedAddress } from '../services/geocode'

type LatLng = [number, number]

// Centrum Polski — fallback gdy inwestycja nie ma jeszcze lat/lng i brak innych pinezek.
const POLAND_CENTER: LatLng = [52.0, 19.0]
const POLAND_ZOOM = 6
const PIN_ZOOM = 16

const EMPTY_ADDRESS: GeocodedAddress = {
  street: '',
  buildingNumber: '',
  postalCode: '',
  city: '',
  voivodeship: '',
  county: '',
  commune: '',
}

// Pinezka jako divIcon (HTML) — ten sam pomysł co w portfolio TemplateMap, bez problemu
// z domyślnymi obrazkami markerów Leaflet pod bundlerem.
function pinIcon(color: string): L.DivIcon {
  return L.divIcon({
    className: '',
    html: `<div style="position:relative;width:30px;height:42px;">
      <svg width="30" height="42" viewBox="0 0 30 42" xmlns="http://www.w3.org/2000/svg">
        <path d="M15 0C6.7 0 0 6.7 0 15c0 10.5 15 27 15 27s15-16.5 15-27C30 6.7 23.3 0 15 0z" fill="${color}" stroke="white" stroke-width="2"/>
        <circle cx="15" cy="15" r="5.5" fill="white"/>
      </svg></div>`,
    iconSize: [30, 42],
    iconAnchor: [15, 42],
    popupAnchor: [0, -38],
  })
}

const CURRENT_ICON = pinIcon('#1976d2') // primary — edytowalna inwestycja
const OTHER_ICON = pinIcon('#9e9e9e') // szary — pozostałe inwestycje (kontekst)

// Obsługa kliknięć mapy:
// - pojedynczy klik ustawia pinezkę tylko gdy jej jeszcze nie ma (placeOnSingleClick),
// - dwuklik ZAWSZE przenosi pinezkę w klikniete miejsce (wygodne, gdy pin jest poza kadrem
//   i przeciąganie jest niepraktyczne).
function MapClickHandlers({
  placeOnSingleClick,
  onPlace,
}: {
  placeOnSingleClick: boolean
  onPlace: (p: LatLng) => void
}) {
  useMapEvents({
    click(e) {
      if (placeOnSingleClick) onPlace([e.latlng.lat, e.latlng.lng])
    },
    dblclick(e) {
      onPlace([e.latlng.lat, e.latlng.lng])
    },
  })
  return null
}

// Recentrowanie mapy przy zmianie wybranej inwestycji.
// Cel liczony bezpośrednio z lat/lng inwestycji (nie ze stale `pos`) — inaczej przy
// szybkim przełączaniu mapa centruje na poprzedniej pozycji, a nowy pin ląduje poza ekranem.
function Recenter({ lat, lng, dep }: { lat: number | null; lng: number | null; dep: string }) {
  const map = useMap()
  useEffect(() => {
    if (lat != null && lng != null) {
      map.setView([lat, lng], PIN_ZOOM)
    } else {
      map.setView(POLAND_CENTER, POLAND_ZOOM)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dep])
  return null
}

// Pozwala programowo przelecieć mapę do nowej pozycji (po wyborze adresu z wyszukiwarki).
function FlyTo({ target }: { target: LatLng | null }) {
  const map = useMap()
  useEffect(() => {
    if (target) map.setView(target, PIN_ZOOM)
  }, [target?.[0], target?.[1]]) // eslint-disable-line react-hooks/exhaustive-deps
  return null
}

export function InvestmentDataPage() {
  const { currentInvestment, userInvestments, patchInvestment } = useInvestmentContext()
  const [pos, setPos] = useState<LatLng | null>(null)
  const [address, setAddress] = useState<GeocodedAddress>(EMPTY_ADDRESS)
  const [flyTarget, setFlyTarget] = useState<LatLng | null>(null)
  const [saving, setSaving] = useState(false)
  const [snack, setSnack] = useState<{ sev: 'success' | 'error'; msg: string } | null>(null)

  // Baseline = stan ostatnio zapisany (start: wartości z inwestycji; po zapisie: bieżące).
  // Względem niego liczymy „dirty" oraz działa „Cofnij" — dlatego po zapisie nie ma już
  // czego cofać (przyciski gasną), mimo że kontekst inwestycji nie jest odświeżany.
  const [baselinePos, setBaselinePos] = useState<LatLng | null>(null)
  const [baselineAddress, setBaselineAddress] = useState<GeocodedAddress>(EMPTY_ADDRESS)

  // Wyszukiwarka adresu (geokoder)
  const [searchInput, setSearchInput] = useState('')
  const [options, setOptions] = useState<GeocodeResult[]>([])
  const [searching, setSearching] = useState(false)

  // Inicjalizacja pozycji + adresu z wybranej inwestycji (reset przy zmianie inwestycji).
  useEffect(() => {
    const initPos: LatLng | null =
      currentInvestment?.lat != null && currentInvestment?.lng != null
        ? [currentInvestment.lat, currentInvestment.lng]
        : null
    const initAddress: GeocodedAddress = {
      street: currentInvestment?.location_street ?? '',
      buildingNumber: currentInvestment?.location_building_number ?? '',
      postalCode: currentInvestment?.location_postal_code ?? '',
      city: currentInvestment?.location_city ?? '',
      voivodeship: currentInvestment?.location_voivodeship ?? '',
      county: currentInvestment?.location_county ?? '',
      commune: currentInvestment?.location_commune ?? '',
    }
    setPos(initPos)
    setAddress(initAddress)
    setBaselinePos(initPos)
    setBaselineAddress(initAddress)
    setSearchInput('')
    setOptions([])
  }, [currentInvestment?.id])

  // Debounce wyszukiwania adresu (Nominatim rate limit).
  useEffect(() => {
    const q = searchInput.trim()
    if (q.length < 3) {
      setOptions([])
      return
    }
    setSearching(true)
    const handle = setTimeout(async () => {
      try {
        setOptions(await searchGeocode(q))
      } finally {
        setSearching(false)
      }
    }, 500)
    return () => clearTimeout(handle)
  }, [searchInput])

  // Pozostałe inwestycje tego samego tenanta z ustawioną lokalizacją (kontekst, nieedytowalne).
  const others = useMemo(
    () =>
      userInvestments.filter(
        (i) =>
          i.id !== currentInvestment?.id &&
          i.tenant_id === currentInvestment?.tenant_id &&
          i.lat != null &&
          i.lng != null,
      ),
    [userInvestments, currentInvestment],
  )

  const label = currentInvestment?.panel_display_label || currentInvestment?.name || ''

  const center: LatLng = pos
    ? pos
    : others[0]
      ? [others[0].lat as number, others[0].lng as number]
      : POLAND_CENTER

  const posDirty = (pos?.[0] ?? null) !== (baselinePos?.[0] ?? null) || (pos?.[1] ?? null) !== (baselinePos?.[1] ?? null)
  const addressDirty = useMemo(
    () => (Object.keys(EMPTY_ADDRESS) as (keyof GeocodedAddress)[]).some((k) => address[k] !== baselineAddress[k]),
    [address, baselineAddress],
  )
  const dirty = posDirty || addressDirty

  // Wybór adresu z wyszukiwarki: ustaw pin na geokodowanej pozycji i wypełnij pola adresu.
  // Pinezka pozostaje niezależna — operator może ją potem przeciągnąć.
  function onPickAddress(result: GeocodeResult | null) {
    if (!result) return
    setPos([result.lat, result.lng])
    setFlyTarget([result.lat, result.lng])
    setAddress(result.address)
  }

  function setField(field: keyof GeocodedAddress, value: string) {
    setAddress((a) => ({ ...a, [field]: value }))
  }

  // Cofnij — przywróć niezapisane zmiany do baseline (działa tylko gdy są zmiany,
  // czyli do momentu zapisu). Po zapisie baseline = zapisane, więc nie ma czego cofać.
  function revert() {
    setPos(baselinePos)
    setAddress(baselineAddress)
    setFlyTarget(baselinePos)
    setSearchInput('')
    setOptions([])
  }

  async function save() {
    if (!currentInvestment) return
    setSaving(true)
    const { data, error } = await supabase.rpc('update_investment_portfolio', {
      p_investment_id: currentInvestment.id,
      ...(pos ? { p_lat: pos[0], p_lng: pos[1] } : {}),
      p_location_street: address.street,
      p_location_building_number: address.buildingNumber,
      p_location_postal_code: address.postalCode,
      p_location_city: address.city,
      p_location_voivodeship: address.voivodeship,
      p_location_county: address.county,
      p_location_commune: address.commune,
    })
    setSaving(false)
    const result = data as { success?: boolean; error?: string } | null
    if (error || result?.success === false) {
      setSnack({ sev: 'error', msg: error?.message || result?.error || 'Błąd zapisu' })
    } else {
      // Nowy baseline = zapisany stan → „Cofnij"/„Zapisz" gasną.
      setBaselinePos(pos)
      setBaselineAddress(address)
      // Synchronizuj kontekst, żeby przełączenie i powrót pokazały świeże dane.
      patchInvestment(currentInvestment.id, {
        ...(pos ? { lat: pos[0], lng: pos[1] } : {}),
        location_street: address.street,
        location_building_number: address.buildingNumber,
        location_postal_code: address.postalCode,
        location_city: address.city,
        location_voivodeship: address.voivodeship,
        location_county: address.county,
        location_commune: address.commune,
      })
      setSnack({ sev: 'success', msg: 'Zapisano dane inwestycji' })
    }
  }

  if (!currentInvestment) {
    return (
      <Box p={3}>
        <Alert severity="info">Wybierz inwestycję w menu po lewej.</Alert>
      </Box>
    )
  }

  return (
    <Box p={3}>
      <Typography variant="h5" gutterBottom>
        Dane inwestycji — {label}
      </Typography>

      <Typography variant="body2" color="textSecondary" gutterBottom>
        {pos
          ? 'Pinezka jest niezależna od adresu — przeciągnij ją lub kliknij dwukrotnie na mapie, aby ją przenieść, potem „Zapisz”.'
          : 'Wyszukaj adres albo kliknij (lub kliknij dwukrotnie) na mapie, aby postawić pinezkę.'}
      </Typography>

      <Paper variant="outlined" sx={{ height: 480, borderRadius: 2, overflow: 'hidden', mb: 2 }}>
        <MapContainer
          center={center}
          zoom={pos ? PIN_ZOOM : POLAND_ZOOM}
          doubleClickZoom={false} // dwuklik przenosi pinezkę, nie przybliża
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          />
          <Recenter lat={currentInvestment.lat} lng={currentInvestment.lng} dep={currentInvestment.id} />
          <FlyTo target={flyTarget} />
          <MapClickHandlers placeOnSingleClick={!pos} onPlace={setPos} />

          {others.map((i) => (
            <Marker key={i.id} position={[i.lat as number, i.lng as number]} icon={OTHER_ICON}>
              <Popup>{i.panel_display_label || i.name}</Popup>
            </Marker>
          ))}

          {pos && (
            <Marker
              position={pos}
              draggable
              icon={CURRENT_ICON}
              eventHandlers={{
                dragend: (e) => {
                  const m = (e.target as L.Marker).getLatLng()
                  setPos([m.lat, m.lng])
                },
              }}
            >
              <Popup>{label} — przeciągnij, aby zmienić</Popup>
            </Marker>
          )}
        </MapContainer>
      </Paper>

      {/* Wyszukiwarka adresu — bezpośrednio nad polami adresu */}
      <Autocomplete
        sx={{ mb: 2, maxWidth: 900 }}
        options={options}
        loading={searching}
        filterOptions={(x) => x} // wyniki z serwera, bez lokalnego filtrowania
        getOptionLabel={(o) => (typeof o === 'string' ? o : o.label)}
        onInputChange={(_, v) => setSearchInput(v)}
        onChange={(_, v) => onPickAddress(v as GeocodeResult | null)}
        noOptionsText={searchInput.trim().length < 3 ? 'Wpisz min. 3 znaki' : 'Brak wyników'}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Wyszukaj adres (ustawia pinezkę)"
            placeholder="np. Rybitew 3, Pułtusk"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {searching ? <CircularProgress size={18} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />

      {/* Pola adresu (edytowalne ręcznie) */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
          gap: 2,
          mb: 2,
          maxWidth: 900,
        }}
      >
        <TextField label="Ulica" size="small" value={address.street} onChange={(e) => setField('street', e.target.value)} />
        <TextField label="Nr budynku" size="small" value={address.buildingNumber} onChange={(e) => setField('buildingNumber', e.target.value)} />
        <TextField label="Kod pocztowy" size="small" value={address.postalCode} onChange={(e) => setField('postalCode', e.target.value)} />
        <TextField label="Miejscowość" size="small" value={address.city} onChange={(e) => setField('city', e.target.value)} />
        <TextField label="Gmina" size="small" value={address.commune} onChange={(e) => setField('commune', e.target.value)} />
        <TextField label="Powiat" size="small" value={address.county} onChange={(e) => setField('county', e.target.value)} />
        <TextField label="Województwo" size="small" value={address.voivodeship} onChange={(e) => setField('voivodeship', e.target.value)} />
      </Box>

      <Stack direction="row" spacing={2} alignItems="center">
        <Button variant="contained" disabled={!dirty || saving} onClick={save}>
          {saving ? 'Zapisywanie…' : 'Zapisz'}
        </Button>
        <Button variant="outlined" disabled={!dirty || saving} onClick={revert}>
          Cofnij
        </Button>
        {pos && (
          <Typography variant="body2" color="textSecondary">
            lat: {pos[0].toFixed(6)}, lng: {pos[1].toFixed(6)}
            {dirty ? ' (niezapisane)' : ''}
          </Typography>
        )}
      </Stack>

      <Snackbar
        open={!!snack}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snack ? (
          <Alert severity={snack.sev} onClose={() => setSnack(null)} sx={{ width: '100%' }}>
            {snack.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  )
}
